import { useEffect, useState } from 'react';
import DatePicker from '../../components/ui/DatePicker';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { communityApi } from '../../api/community.api';
import { usersApi } from '../../api/users.api';
import CommunityTripMap from '../../components/community/CommunityTripMap';
import type { ActivityType, CommunityTripDetail, Venue, VenueCategory } from '../../types';
import { useAuthStore } from '../../stores/auth.store';
import { StarRating } from '../../components/community/StarRating';
import Spinner from '../../components/ui/Spinner';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { toast } from '../../components/ui/Toast';
import { UserAvatar } from '../../components/ui/UserAvatar';
import {
  Icon, ArrowLeft, Copy, MapPin, CalendarDays, BedDouble, Users,
  Utensils, Coffee, Landmark, Eye, Building2, TreePine, ShoppingBag, Sparkles,
  Bus, Hotel as HotelIcon, Waves, Mountain, SquareParking, ExternalLink, FileText, Clock,
  Lightbulb,
} from '../../components/ui/Icon';
import { CAT_HEX, catHex, catIcon } from '../../components/trips/expenses/meta';
import type { ExpenseCategory } from '../../types';
import { format, formatDistanceToNow } from 'date-fns';
import { useDateLocale } from '../../utils/dateLocale';
import { PhotoCredit } from '../../components/ui/PhotoCredit';

type Tab = 'itinerary' | 'places' | 'map' | 'tips' | 'expenses';

// ── Activity meta ─────────────────────────────────────────────────────────────
const TYPE_ICON: Record<ActivityType, React.ElementType> = {
  RESTAURANT: Utensils, CAFE: Coffee, MONUMENT: Landmark, VIEWPOINT: Eye,
  NEIGHBORHOOD: Building2, PARK: TreePine, SHOP: ShoppingBag,
  EXPERIENCE: Sparkles, TRANSPORT: Bus, PARKING: SquareParking, BEACH: Waves, NATURE: Mountain, ACCOMMODATION: HotelIcon, OTHER: MapPin,
};
const TYPE_COLOR: Record<ActivityType, string> = {
  RESTAURANT: 'text-orange-500', CAFE: 'text-amber-600', MONUMENT: 'text-violet-500',
  VIEWPOINT: 'text-sky-500', NEIGHBORHOOD: 'text-indigo-500', PARK: 'text-green-500',
  SHOP: 'text-pink-500', EXPERIENCE: 'text-teal-500', TRANSPORT: 'text-slate-500',
  PARKING: 'text-blue-600', BEACH: 'text-cyan-500', NATURE: 'text-lime-600', ACCOMMODATION: 'text-blue-500', OTHER: 'text-slate-400',
};
const TYPE_BG: Record<ActivityType, string> = {
  RESTAURANT: 'bg-orange-50 dark:bg-orange-900/20',
  CAFE: 'bg-amber-50 dark:bg-amber-900/20',
  MONUMENT: 'bg-violet-50 dark:bg-violet-900/20',
  VIEWPOINT: 'bg-sky-50 dark:bg-sky-900/20',
  NEIGHBORHOOD: 'bg-indigo-50 dark:bg-indigo-900/20',
  PARK: 'bg-green-50 dark:bg-green-900/20',
  SHOP: 'bg-pink-50 dark:bg-pink-900/20',
  EXPERIENCE: 'bg-teal-50 dark:bg-teal-900/20',
  TRANSPORT: 'bg-slate-100 dark:bg-slate-800',
  PARKING: 'bg-blue-50 dark:bg-blue-900/20',
  BEACH: 'bg-cyan-50 dark:bg-cyan-900/20',
  NATURE: 'bg-lime-50 dark:bg-lime-900/20',
  ACCOMMODATION: 'bg-blue-50 dark:bg-blue-900/20',
  OTHER: 'bg-slate-100 dark:bg-slate-800',
};

// ── Venue meta ────────────────────────────────────────────────────────────────
const VENUE_CAT_ICON: Record<VenueCategory, React.ElementType> = {
  RESTAURANT: Utensils, CAFE: Coffee, ATTRACTION: Landmark, VIEWPOINT: Eye,
  NEIGHBORHOOD: Building2, PARK: TreePine, SHOP: ShoppingBag,
  EXPERIENCE: Sparkles, TRANSPORT_HUB: Bus,
  BEACH: Waves, NATURE: Mountain, PARKING: SquareParking, OTHER: MapPin,
};
const VENUE_CAT_CLS: Record<VenueCategory, string> = {
  RESTAURANT: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20',
  CAFE: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20',
  ATTRACTION: 'text-violet-500 bg-violet-50 dark:bg-violet-900/20',
  VIEWPOINT: 'text-sky-500 bg-sky-50 dark:bg-sky-900/20',
  NEIGHBORHOOD: 'text-indigo-500 bg-indigo-50 dark:bg-indigo-900/20',
  PARK: 'text-green-500 bg-green-50 dark:bg-green-900/20',
  SHOP: 'text-pink-500 bg-pink-50 dark:bg-pink-900/20',
  EXPERIENCE: 'text-teal-500 bg-teal-50 dark:bg-teal-900/20',
  TRANSPORT_HUB: 'text-slate-500 bg-slate-100 dark:bg-slate-800',
  BEACH: 'text-cyan-500 bg-cyan-50 dark:bg-cyan-900/20',
  NATURE: 'text-lime-600 bg-lime-50 dark:bg-lime-900/20',
  PARKING: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20',
  OTHER: 'text-stone-500 bg-stone-100 dark:bg-stone-800',
};

const DEST_THEMES: [string, { from: string; to: string }][] = [
  ['paris',     { from: '#5c3a6e', to: '#2d1a3e' }],
  ['london',    { from: '#2a3547', to: '#141c28' }],
  ['new york',  { from: '#6b4228', to: '#3a2010' }],
  ['lisboa',    { from: '#1a5c6e', to: '#0b3240' }],
  ['lisbon',    { from: '#1a5c6e', to: '#0b3240' }],
  ['madrid',    { from: '#7a3320', to: '#3e1a0e' }],
  ['barcelona', { from: '#7a5c1a', to: '#3e2e0a' }],
  ['rome',      { from: '#7a4020', to: '#3e200e' }],
  ['tokyo',     { from: '#6e2845', to: '#3a1424' }],
  ['dubai',     { from: '#7a6218', to: '#3e320c' }],
  ['amsterdam', { from: '#1a547a', to: '#0b2c40' }],
  ['berlin',    { from: '#323845', to: '#181c24' }],
  ['prague',    { from: '#432878', to: '#22143e' }],
  ['vienna',    { from: '#1a5c48', to: '#0b3228' }],
  ['athens',    { from: '#1a4878', to: '#0b2440' }],
  ['bali',      { from: '#1a6038', to: '#0b321c' }],
];
function getTheme(dest: string) {
  const lower = dest.toLowerCase();
  return DEST_THEMES.find(([k]) => lower.includes(k))?.[1] ?? { from: '#1e3048', to: '#0d1824' };
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function VenueRow({ venue }: { venue: Venue }) {
  const { t } = useTranslation();
  const cls       = VENUE_CAT_CLS[venue.category] ?? 'text-slate-400 bg-slate-100 dark:bg-slate-800';
  const IconComp  = VENUE_CAT_ICON[venue.category] ?? MapPin;
  return (
    <div className="flex items-start gap-3 py-3.5 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cls}`}>
        <Icon as={IconComp} size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{venue.name}</p>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
          {venue.zone    && <span className="text-xs text-slate-400">{venue.zone}</span>}
          {venue.zone && venue.address && <span className="text-slate-200 dark:text-slate-700 text-xs">·</span>}
          {venue.address && <span className="text-xs text-slate-400 truncate">{venue.address}</span>}
        </div>
        {venue.priceInfo && <span className="font-mono text-[10px] text-slate-400 mt-0.5 block">{venue.priceInfo}</span>}
        {venue.notes    && <p className="text-xs text-slate-400 italic mt-0.5 break-words">{venue.notes}</p>}
      </div>
      {(venue.mapsUrl || venue.bookingUrl) && (
        <a
          href={venue.mapsUrl ?? venue.bookingUrl ?? '#'}
          target="_blank" rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="text-slate-300 hover:text-teal-500 dark:text-slate-600 dark:hover:text-teal-400 transition-colors shrink-0 mt-1"
          aria-label={t('explore.detail.openInMaps')}
        >
          <Icon as={ExternalLink} size={13} />
        </a>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CommunityTripDetailPage() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { tripId } = useParams<{ tripId: string }>();
  const navigate   = useNavigate();
  const currentUser = useAuthStore(s => s.user);
  const [trip, setTrip]               = useState<CommunityTripDetail | null>(null);
  const [loading, setLoading]         = useState(true);
  const [tab, setTab]                 = useState<Tab>('itinerary');
  const [myRating, setMyRating]       = useState<number | null>(null);
  const [avgRating, setAvgRating]     = useState<number | null>(null);
  const [ratingCount, setRatingCount] = useState(0);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [cloning, setCloning]         = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneStartDate, setCloneStartDate] = useState('');
  const [cloneName, setCloneName]           = useState('');
  const [cloneSelectedDayIds, setCloneSelectedDayIds] = useState<string[]>([]);
  const [following, setFollowing]           = useState(false);
  const [followLoading, setFollowLoading]   = useState(false);

  useEffect(() => {
    communityApi.get(tripId!)
      .then(data => {
        setTrip(data);
        setMyRating(data.myRating);
        setAvgRating(data.avgRating);
        setRatingCount(data.ratingCount);
        setFollowing(data.isFollowing);
      })
      .catch(() => toast(t('explore.toast.tripNotFound'), 'error'))
      .finally(() => setLoading(false));
  }, [tripId]);

  async function handleRate(stars: number) {
    if (!trip || ratingLoading) return;
    setMyRating(stars);
    setRatingLoading(true);
    try {
      const result = await communityApi.rate(tripId!, stars);
      setAvgRating(result.avgRating);
      setRatingCount(result.ratingCount);
    } catch {
      toast(t('explore.toast.ratingFailed'), 'error');
      setMyRating(trip.myRating);
    } finally {
      setRatingLoading(false);
    }
  }

  async function handleToggleFollow() {
    if (!trip || followLoading) return;
    setFollowLoading(true);
    try {
      const result = await usersApi.toggleFollow(trip.owner.id);
      setFollowing(result.following);
    } catch {
      toast(t('explore.toast.followFailed'), 'error');
    } finally {
      setFollowLoading(false);
    }
  }

  function openCloneModal() {
    setCloneName(trip?.name ?? '');
    setCloneStartDate('');
    setCloneSelectedDayIds(trip ? trip.days.map(d => d.id) : []);
    setCloneModalOpen(true);
  }

  function toggleCloneDay(dayId: string) {
    setCloneSelectedDayIds(prev =>
      prev.includes(dayId) ? prev.filter(x => x !== dayId) : [...prev, dayId],
    );
  }

  function selectAllCloneDays() {
    if (!trip) return;
    setCloneSelectedDayIds(trip.days.map(d => d.id));
  }

  function clearAllCloneDays() {
    setCloneSelectedDayIds([]);
  }

  async function handleClone() {
    if (cloning) return;
    if (cloneSelectedDayIds.length === 0) {
      toast(t('explore.clone.selectAtLeastOne'), 'error');
      return;
    }
    setCloning(true);
    try {
      const allSelected = trip ? cloneSelectedDayIds.length === trip.days.length : false;
      const newTrip = await communityApi.clone(tripId!, {
        startDate: cloneStartDate || undefined,
        name:      cloneName.trim() || undefined,
        // Only send selectedDayIds if not all are selected (keeps backward behavior)
        selectedDayIds: allSelected ? undefined : cloneSelectedDayIds,
      });
      setCloneModalOpen(false);
      toast(t('explore.toast.cloned'), 'success');
      navigate(`/trips/${newTrip.id}/itinerary`);
    } catch (err: any) {
      const code = err?.response?.data?.error;
      toast(code === 'INSUFFICIENT_CREDITS' ? t('explore.toast.insufficientCredits') : t('explore.toast.cloneFailed'), 'error');
    } finally {
      setCloning(false);
    }
  }

  if (loading) return <LoadingScreen />;
  if (!trip)   return <div className="flex justify-center py-24 text-slate-400">{t('explore.toast.tripNotFound')}</div>;

  const theme         = getTheme(trip.destination);
  const authorName    = trip.owner.displayName || trip.owner.username;
  const totalActivities = trip.days.reduce((s, d) => s + d.activities.length, 0);
  const isOwnTrip     = currentUser?.id === trip.owner.id;

  const heroStyle = trip.coverImageUrl
    ? { backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0.65)), url(${trip.coverImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: `linear-gradient(135deg, ${theme.from}, ${theme.to})` };

  const tabs = ([
    { key: 'itinerary' as Tab, label: t('explore.detail.tabs.itinerary'),  count: totalActivities },
    { key: 'places'    as Tab, label: t('explore.detail.tabs.places'),     count: trip.venues.length },
    { key: 'map'       as Tab, label: t('explore.detail.tabs.map') },
    { key: 'tips'      as Tab, label: t('explore.detail.tabs.tips'), count: trip.tips.length, hidden: trip.tips.length === 0 },
    { key: 'expenses'  as Tab, label: t('explore.detail.tabs.expenses'),      hidden: !trip.expenseSummary },
  ] as { key: Tab; label: string; count?: number; hidden?: boolean }[]).filter(item => !item.hidden);

  // Group tips by category
  const tipsByCategory = trip.tips.reduce((acc, tip) => {
    (acc[tip.category] ??= []).push(tip);
    return acc;
  }, {} as Record<string, typeof trip.tips>);

  return (
    <div className="flex flex-col h-full overflow-hidden animate-fade-in">

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <div className="shrink-0 relative" style={heroStyle}>
        {!trip.coverImageUrl && (
          <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
        )}
        <PhotoCredit
          name={trip.coverImagePhotographer}
          url={trip.coverImagePhotographerUrl}
          className="absolute bottom-1.5 right-4 md:right-8 z-20 max-w-[60%]"
        />
        <div className="relative z-10 px-4 md:px-8 pt-4 pb-4">
          {/* Back + author row */}
          <div className="flex items-center gap-3 mb-3">
            <button
              onClick={() => navigate('/explore')}
              className="flex items-center gap-1 text-white/50 hover:text-white/90 text-xs font-medium transition-colors cursor-pointer group shrink-0"
            >
              <Icon as={ArrowLeft} size={12} className="group-hover:-translate-x-0.5 transition-transform" />
              {t('explore.detail.backToExplore')}
            </button>
            <span className="text-white/20 text-xs">·</span>
            <span className="font-mono text-[10px] text-white/40">@{authorName}</span>
          </div>

          {/* Title + meta in one compact block */}
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1
                className="font-serif font-medium text-white leading-none truncate"
                style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)' }}
              >
                {trip.name}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-white/55 text-[11px]">
                <span className="flex items-center gap-1"><Icon as={MapPin} size={10} />{trip.destination}</span>
                <span className="text-white/20">·</span>
                <span className="flex items-center gap-1">
                  <Icon as={CalendarDays} size={10} />
                  {format(new Date(trip.startDate), 'd MMM', { locale: dateLocale })} – {format(new Date(trip.endDate), 'd MMM yyyy', { locale: dateLocale })}
                </span>
                <span className="text-white/20">·</span>
                <span>{trip.nights}n · {trip.participantCount}p · {totalActivities}✦</span>
              </div>
            </div>
            {(avgRating !== null || ratingCount > 0) && (
              <div className="flex items-center gap-1.5 shrink-0">
                <StarRating value={avgRating} size="sm" />
                <span className="text-white/80 font-semibold text-sm">{avgRating?.toFixed(1) ?? '–'}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Tab bar ───────────────────────────────────────────────── */}
      <div className="shrink-0 flex items-center gap-0 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 md:px-8 overflow-x-auto scrollbar-none">
        {tabs.map(tabItem => (
          <button
            key={tabItem.key}
            onClick={() => setTab(tabItem.key)}
            className={`flex items-center gap-1.5 px-1 mr-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors cursor-pointer whitespace-nowrap shrink-0 ${
              tab === tabItem.key
                ? 'border-slate-900 dark:border-white text-slate-900 dark:text-white'
                : 'border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            {tabItem.label}
            {tabItem.count !== undefined && tabItem.count > 0 && (
              <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${
                tab === tabItem.key
                  ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-400'
              }`}>
                {tabItem.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Body ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-950">
        {/* Map tab: full-width, no sidebar */}
        {tab === 'map' && (
          <div className="px-4 md:px-8 py-6">
            <CommunityTripMap trip={trip} />
          </div>
        )}
        <div className={tab === 'map' ? 'hidden' : 'px-4 md:px-8 py-6 lg:grid lg:grid-cols-[1fr_300px] lg:gap-8 lg:items-start'}>

          {/* ── Main content ── */}
          <div>

            {/* Itinerary */}
            {tab === 'itinerary' && (
              <div className="space-y-6">
                {trip.days.length === 0 && <p className="text-sm text-slate-400 italic">{t('explore.detail.noItinerary')}</p>}
                {trip.days.map(day => {
                  const activities = [...day.activities].sort((a, b) => a.sortOrder - b.sortOrder);

                  return (
                    <div key={day.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                        <div className="w-8 h-8 rounded-lg bg-slate-900 dark:bg-white flex items-center justify-center shrink-0">
                          <span className="font-mono text-[11px] font-bold text-white dark:text-slate-900">{day.dayNumber}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-[10px] tracking-widest uppercase text-slate-400">
                            {format(new Date(day.date), 'EEE, d MMM', { locale: dateLocale })}
                          </p>
                          {day.title && <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 leading-tight">{day.title}</p>}
                        </div>
                        <span className="font-mono text-[10px] text-slate-300 dark:text-slate-700 shrink-0">
                          {t('explore.detail.activities', { count: day.activities.length })}
                        </span>
                      </div>

                      {activities.length === 0 ? (
                        <p className="text-sm text-slate-400 italic px-5 py-4">{t('explore.detail.noActivities')}</p>
                      ) : (
                        <div className="space-y-1.5 px-5 py-3">
                          {activities.map(act => {
                            const IconComp   = TYPE_ICON[act.type] ?? MapPin;
                            const colorClass = TYPE_COLOR[act.type] ?? 'text-slate-400';
                            const bgClass    = TYPE_BG[act.type]   ?? 'bg-slate-50 dark:bg-slate-800';
                            return (
                              <div key={act.id} className={`flex items-start gap-3 px-3 py-2.5 rounded-xl ${bgClass}`}>
                                <Icon as={IconComp} size={14} className={`${colorClass} shrink-0 mt-0.5`} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-snug">{act.title}</p>
                                  {act.venueName && act.venueName !== act.title && <p className="text-xs text-slate-400 mt-0.5">{act.venueName}</p>}
                                  {act.zone && <p className="text-xs text-slate-400">{act.zone}</p>}
                                  {act.notes && <p className="text-xs text-slate-400 italic mt-1 break-words">{act.notes}</p>}
                                </div>
                                {act.startTime && (
                                  <div className="flex items-center gap-1 text-slate-400 shrink-0">
                                    <Icon as={Clock} size={10} />
                                    <span className="font-mono text-[10px]">{act.startTime}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Places */}
            {tab === 'places' && (
              <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 px-5">
                {trip.venues.length === 0
                  ? <p className="text-sm text-slate-400 italic py-6">{t('explore.detail.noPlaces')}</p>
                  : trip.venues.map(venue => <VenueRow key={venue.id} venue={venue} />)
                }
              </div>
            )}


            {/* Tips */}
            {tab === 'tips' && (
              <div className="space-y-4">
                {trip.tips.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">{t('explore.detail.noTips')}</p>
                ) : Object.entries(tipsByCategory).map(([category, tips]) => (
                  <div key={category} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 dark:border-slate-800">
                      <Icon as={Lightbulb} size={13} className="text-amber-500" />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">{category}</span>
                    </div>
                    <div className="divide-y divide-slate-50 dark:divide-slate-800/60">
                      {tips.map(tip => (
                        <div key={tip.id} className="px-5 py-3.5">
                          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{tip.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Budget / Expenses */}
            {tab === 'expenses' && (
              <div className="space-y-4">
                {/* Actual spend by category */}
                {trip.expenseSummary && (
                  <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-5">
                    <div className="flex items-baseline justify-between mb-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{t('explore.detail.actualSpend')}</p>
                      <p className="font-bold text-xl text-slate-900 dark:text-slate-100">
                        {trip.expenseSummary.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        <span className="text-sm font-normal text-slate-400 ml-1">{trip.expenseSummary.currency}</span>
                      </p>
                    </div>
                    <div className="space-y-2.5">
                      {Object.entries(trip.expenseSummary.byCategory)
                        .sort(([, a], [, b]) => b - a)
                        .map(([cat, amt]) => {
                          const hex   = catHex(cat);
                          const label = t(`expenses.categories.${cat in CAT_HEX ? cat as ExpenseCategory : 'OTHER'}`);
                          const pct  = Math.round((amt / trip.expenseSummary!.total) * 100);
                          return (
                            <div key={cat}>
                              <div className="flex items-center gap-3 mb-1">
                                <div
                                  className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0"
                                  style={{ background: hex + '22' }}
                                >
                                  <Icon as={catIcon(cat)} size={12} style={{ color: hex }} />
                                </div>
                                <span className="text-sm text-slate-700 dark:text-slate-300 flex-1">{label}</span>
                                <span className="font-mono text-xs text-slate-500">
                                  {amt.toLocaleString(undefined, { maximumFractionDigits: 0 })} {trip.expenseSummary!.currency}
                                </span>
                                <span className="font-mono text-[10px] text-slate-400 w-8 text-right">{pct}%</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden ml-9">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{ width: `${pct}%`, background: hex }}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* ── Sticky sidebar ── */}
          <aside className="hidden lg:block">
            <div className="sticky top-6 space-y-3">

              {/* Clone CTA */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-5">
                <button
                  onClick={openCloneModal}
                  disabled={cloning}
                  className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-semibold hover:bg-slate-700 dark:hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-60 mb-3"
                >
                  {cloning ? <Spinner size="sm" /> : <><Icon as={Copy} size={15} />{t('explore.detail.useAsTemplate')}</>}
                </button>
                {trip.cloneCount > 0 && (
                  <p className="text-center font-mono text-[10px] text-slate-400">
                    {t('explore.detail.used')} {t('explore.detail.times', { count: trip.cloneCount })}
                  </p>
                )}
              </div>

              {/* Rating */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-3">{t('explore.detail.communityRating')}</p>
                <div className="flex items-center gap-2.5 mb-1">
                  <StarRating value={avgRating} size="md" />
                  <span className="font-bold text-xl text-slate-900 dark:text-slate-100 leading-none">{avgRating?.toFixed(1) ?? '–'}</span>
                </div>
                <p className="text-xs text-slate-400 mb-4">
                  {ratingCount === 0 ? t('explore.detail.noRatingsYet') : t('explore.detail.ratings', { count: ratingCount })}
                </p>
                {!isOwnTrip && (
                  <div className="border-t border-slate-100 dark:border-slate-800 pt-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">{t('explore.detail.rateThisTrip')}</p>
                    <StarRating value={myRating} onChange={handleRate} size="md" />
                    {myRating && <p className="text-xs text-teal-500 mt-2">{t('explore.detail.youRated', { count: myRating })}</p>}
                  </div>
                )}
              </div>

              {/* Trip stats */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-3">{t('explore.detail.tripDetails')}</p>
                <div className="space-y-2.5">
                  {([
                    { icon: MapPin,       label: t('explore.detail.stats.destination'), value: trip.destination },
                    { icon: CalendarDays, label: t('explore.detail.stats.dates'),       value: `${format(new Date(trip.startDate), 'd MMM', { locale: dateLocale })} – ${format(new Date(trip.endDate), 'd MMM yyyy', { locale: dateLocale })}` },
                    { icon: BedDouble,    label: t('explore.detail.stats.duration'),    value: t('explore.detail.stats.nights', { count: trip.nights }) },
                    { icon: Users,        label: t('explore.detail.stats.people'),      value: String(trip.participantCount) },
                    { icon: Sparkles,     label: t('explore.detail.stats.activities'),  value: String(totalActivities) },
                  ] as const).map(({ icon, label, value }) => (
                    <div key={label} className="flex items-start gap-2.5">
                      <Icon as={icon} size={14} className="text-slate-400 shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">{label}</p>
                        <p className="text-sm text-slate-700 dark:text-slate-300 font-medium">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Author */}
              <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-5">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-3">{t('explore.detail.author')}</p>
                <div className="flex items-center gap-3 mb-3">
                  <UserAvatar name={authorName} avatarUrl={trip.owner.avatarUrl} size={36} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{authorName}</p>
                    <p className="font-mono text-[10px] text-slate-400">
                      {t('explore.detail.publishedAgo', { time: formatDistanceToNow(new Date(trip.publishedAt), { locale: dateLocale }) })}
                    </p>
                  </div>
                </div>
                {!isOwnTrip && (
                  <button
                    onClick={handleToggleFollow}
                    disabled={followLoading}
                    className={`w-full h-9 rounded-xl text-xs font-semibold transition-colors cursor-pointer disabled:opacity-60 ${
                      following
                        ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 border border-teal-200 dark:border-teal-800 hover:bg-teal-100 dark:hover:bg-teal-900/40'
                        : 'border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    {followLoading ? '…' : following ? t('explore.detail.following') : t('explore.detail.follow')}
                  </button>
                )}
              </div>

            </div>
          </aside>

          {/* Mobile bottom bar */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 z-20 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-4 py-3 flex items-center gap-3">
            {!isOwnTrip && (
              <div className="flex-1">
                <StarRating value={myRating} onChange={handleRate} size="md" />
                <p className="font-mono text-[9px] text-slate-400 mt-0.5">{avgRating?.toFixed(1) ?? '–'} · {t('explore.detail.ratings', { count: ratingCount })}</p>
              </div>
            )}
            <button
              onClick={openCloneModal}
              disabled={cloning}
              className="flex items-center gap-2 h-11 px-5 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-semibold shrink-0 cursor-pointer disabled:opacity-60"
            >
              {cloning ? <Spinner size="sm" /> : <><Icon as={Copy} size={14} />{t('explore.detail.useAsTemplate')}</>}
            </button>
          </div>
          <div className="lg:hidden h-20" />

        </div>
      </div>

      {/* ── Clone modal (adapt & clone) ──────────────────────────────── */}
      {cloneModalOpen && trip && (() => {
        const selectedCount = cloneSelectedDayIds.length;
        const newNights = Math.max(0, selectedCount - 1);
        const previewEnd = cloneStartDate && selectedCount > 0
          ? (() => {
              const d = new Date(cloneStartDate);
              d.setDate(d.getDate() + newNights);
              return format(d, 'd MMM yyyy', { locale: dateLocale });
            })()
          : null;
        const allSelected = selectedCount === trip.days.length;
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => !cloning && setCloneModalOpen(false)}
          >
            <div
              className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-fade-in"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-1">{t('explore.clone.title')}</p>
                <h2 className="font-serif font-medium text-slate-900 dark:text-slate-100 text-2xl leading-tight">
                  {trip.name}
                </h2>
                <p className="text-sm text-slate-500 mt-1">{trip.destination} · {t('explore.detail.stats.nights', { count: trip.nights })}</p>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                {/* Trip name */}
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                    {t('explore.clone.tripName')}
                  </label>
                  <input
                    type="text"
                    value={cloneName}
                    onChange={e => setCloneName(e.target.value)}
                    placeholder={trip.name}
                    className="w-full h-11 px-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 outline-none focus:border-teal-400 transition-colors placeholder:text-slate-300 dark:placeholder:text-slate-600"
                  />
                </div>

                {/* Date picker */}
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                    {t('explore.clone.whenLabel')}
                  </label>
                  <DatePicker value={cloneStartDate} onChange={setCloneStartDate} />
                  {previewEnd && (
                    <p className="text-xs text-slate-400 mt-2">
                      {t('explore.clone.tripEnds')} <span className="font-medium text-slate-600 dark:text-slate-300">{previewEnd}</span>
                    </p>
                  )}
                  {!cloneStartDate && (
                    <p className="text-xs text-slate-400 mt-2">{t('explore.clone.keepOriginalDates')}</p>
                  )}
                </div>

                {/* Day selection */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      {t('explore.clone.daysLabel')}
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={allSelected ? clearAllCloneDays : selectAllCloneDays}
                        className="font-mono text-[10px] uppercase tracking-widest text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors cursor-pointer"
                      >
                        {allSelected ? t('explore.clone.clearAll') : t('explore.clone.selectAll')}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {trip.days.map((day, idx) => {
                      const checked = cloneSelectedDayIds.includes(day.id);
                      const acts = day.activities ?? [];
                      const dayDate = day.date ? new Date(day.date) : null;
                      return (
                        <button
                          key={day.id}
                          type="button"
                          onClick={() => toggleCloneDay(day.id)}
                          className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all cursor-pointer ${
                            checked
                              ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-300 dark:border-teal-700'
                              : 'bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                          }`}
                        >
                          <span className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-colors ${
                            checked
                              ? 'bg-teal-600 dark:bg-teal-500 text-white'
                              : 'border-2 border-slate-300 dark:border-slate-600'
                          }`}>
                            {checked && (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                                {t('itinerary.dayLabel', { n: idx + 1 })}
                              </span>
                              {dayDate && (
                                <span className="font-mono text-[10px] text-slate-400">
                                  · {format(dayDate, 'd MMM', { locale: dateLocale })}
                                </span>
                              )}
                            </div>
                            <p className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate">
                              {day.title || t('explore.clone.untitledDay')}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              {t('explore.clone.activitiesCount', { count: acts.length })}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800">
                <div className="flex items-center justify-between gap-4 mb-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('explore.clone.summaryDays', { count: selectedCount })} · {t('explore.clone.summaryNights', { count: newNights })}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setCloneModalOpen(false)}
                    disabled={cloning}
                    className="flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleClone}
                    disabled={cloning || selectedCount === 0}
                    className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-semibold hover:bg-slate-700 dark:hover:bg-slate-100 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {cloning ? <Spinner size="sm" /> : <><Icon as={Copy} size={14} />{t('explore.clone.addToMyTrips')}</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
